import { isEmpty } from '@ember/utils';
import { reStringNeedsEscaping } from 'cio/utils/objects';
import { isNumeric } from 'cio/utils/numbers';
import AttributeCondition from 'cio/app-models/conditions/attribute';
import EventAttributeCondition from 'cio/app-models/conditions/event-attribute';

export enum JsonAttributeOperator {
  ArrayIndexHasPropertyOrValue = 'array_index_has_property_or_value', // A[1].B
  ArrayOrObjectHasPropertyOrValue = 'array_or_object_has_property_or_value', // A?.B
  ArrayHasPropertyOrValue = 'array_has_property_or_value', // A[].B
  ObjectHasProperty = 'object_has_property', // A.B
}

export type JsonConditionItem = {
  name: string;
  operator: string;
  arrayConditionType?: string;
  arrayIndex?: number;
};

export const MAX_JSON_DEPTH = 7;

function isLastItemArrayCondition(items: JsonConditionItem[]) {
  if (items.length === 0) {
    return false;
  }

  let lastItem = items[items.length - 1];
  return [
    JsonAttributeOperator.ArrayHasPropertyOrValue,
    JsonAttributeOperator.ArrayOrObjectHasPropertyOrValue,
    JsonAttributeOperator.ArrayIndexHasPropertyOrValue,
  ].includes(lastItem.operator as JsonAttributeOperator);
}

export function stringToConditionItems(str: string) {
  let items: JsonConditionItem[] = [];

  let index = 0;
  let { length } = str;
  let currentWord = '';
  let isEscaping = false;
  let isInSquareBrackets = false;
  let maybeBracketsWord = false;
  let bracketsWord: string | null = null;
  let arrayIndexString: string | null = null;

  while (index < length) {
    let char = str[index];

    if (isEscaping) {
      currentWord += char;
      isEscaping = false;
      index++;
      continue;
    }

    if (isInSquareBrackets) {
      if (char === '"' && str[index - 1] === '[') {
        // We are potentially parsing an escaped object key string
        maybeBracketsWord = true;
        index++;
        continue;
      }

      if (char === '"' && str[index + 1] === ']' && maybeBracketsWord) {
        if (currentWord !== '') {
          items.push({
            name: currentWord,
            operator: JsonAttributeOperator.ObjectHasProperty,
          });
        }
        currentWord = bracketsWord ?? '';
        bracketsWord = null;
        index += 2;
        maybeBracketsWord = false;
        isInSquareBrackets = false;
        arrayIndexString = null;
        continue;
      }

      if (maybeBracketsWord) {
        if (bracketsWord == null) {
          bracketsWord = '';
        }

        bracketsWord += char;
        index++;
        continue;
      }

      if (char === ']') {
        let item: JsonConditionItem = {
          name: currentWord,
          operator: JsonAttributeOperator.ArrayHasPropertyOrValue,
        };

        if (arrayIndexString != null) {
          // We've parsed an array index accessor. The "basic" condition editor
          // doesn't support this, so it'll rely on the special operator set
          // here to detect such conditions
          item.operator = JsonAttributeOperator.ArrayIndexHasPropertyOrValue;
          item.arrayIndex = parseInt(arrayIndexString);
        }

        isInSquareBrackets = false;
        arrayIndexString = null;

        if (isLastItemArrayCondition(items)) {
          item.arrayConditionType = isEmpty(currentWord) ? 'value' : 'property';
        }

        items.push(item);
        currentWord = '';
        index++;
        continue;
      }

      let maybeNumber = parseInt(char);

      if (Number.isNaN(maybeNumber) || maybeBracketsWord) {
        // If this isn't a number but there was a [ before, then the attribute
        // name contains a mix of characters and isn't necessarily a JSON string
        // - continue to the next character as if it's a regular attribute
        isInSquareBrackets = false;
        arrayIndexString = null;
        currentWord += char;
        isEscaping = false;
        index++;
        continue;
      } else {
        // If this can be parsed as a number, append it to the index string and
        // continue walking the string (could be a multi-digit number, or an
        // awkward attribute name)
        if (arrayIndexString == null) {
          arrayIndexString = '';
        }

        arrayIndexString += char;
        index++;
        continue;
      }
    }

    if (char === '.') {
      if (currentWord === '' && isLastItemArrayCondition(items)) {
        // Expressions like 'A[].B'
        index++;
        continue;
      }

      let item: JsonConditionItem = {
        name: currentWord,
        operator: JsonAttributeOperator.ObjectHasProperty,
      };

      if (isLastItemArrayCondition(items)) {
        item.arrayConditionType = isEmpty(currentWord) ? 'value' : 'property';
      }

      items.push(item);
      currentWord = '';
      index++;
      continue;
    }

    if (char === '?') {
      let nextChar = str[index + 1];

      if (nextChar === '.' || nextChar === '?' || nextChar == null) {
        let item: JsonConditionItem = {
          name: currentWord,
          operator: JsonAttributeOperator.ArrayOrObjectHasPropertyOrValue,
        };

        if (isLastItemArrayCondition(items)) {
          item.arrayConditionType = isEmpty(currentWord) ? 'value' : 'property';
        }

        items.push(item);
        currentWord = '';
        index++;
        if (nextChar === '.') {
          index++;
        }
        continue;
      }
    }

    if (char === '[') {
      isInSquareBrackets = true;
      index++;
      continue;
    }

    if (char === '\\') {
      // "\" escapes the next character, so we need to ignore any square
      // brackets or dots
      isEscaping = true;
    }

    currentWord += char;
    index++;
  }

  if (!isEmpty(currentWord)) {
    let item: JsonConditionItem = {
      name: currentWord,
      operator: '',
    };

    if (isLastItemArrayCondition(items)) {
      item.arrayConditionType = 'property';
    }

    items.push(item);
  } else if (isLastItemArrayCondition(items)) {
    items.push({
      name: currentWord,
      operator: '',
      arrayConditionType: 'value',
    });
  }

  return items;
}

export function hasAnyJsonConditions(str: string) {
  let conditionItems = stringToConditionItems(str);

  if (conditionItems.length <= 1) {
    return false;
  }

  // We don't support strings like "[].A"
  if (conditionItems[0].name === '' && conditionItems[0].operator === JsonAttributeOperator.ArrayHasPropertyOrValue) {
    return false;
  }

  return true;
}

export function copyJsonConditionToOriginalCondition(
  jsonCondition: AttributeCondition,
  originalCondition: AttributeCondition,
  options?: { escapeFirstElement?: boolean },
): void;
export function copyJsonConditionToOriginalCondition(
  jsonCondition: EventAttributeCondition,
  originalCondition: EventAttributeCondition,
  options: { escapeFirstElement?: boolean },
): void {
  let { escapeFirstElement = true } = options ?? {};

  let pathString = '';
  let currentCondition: typeof jsonCondition | null = jsonCondition;
  let lastCondition: typeof jsonCondition = jsonCondition;

  function updatePathString(item: number | string, accessorType: 'array' | 'object', escape = true) {
    if (typeof item === 'number') {
      pathString = `${pathString}[${item}]`;
    } else {
      // Check whether the object key needs to be escaped. If it does, it needs
      // to be accessed via ["object.key"] rather than with parent.object_key
      // notation
      let needsEscaping = escape && (isNumeric(item) || reStringNeedsEscaping.test(item));
      if (needsEscaping) {
        pathString = `${pathString}["${item}"]`;
      } else {
        // The "where at least one" operator uses "?." to denote array OR object
        // access. A?.B could match either:
        // - an array of objects like A = [ { B: 'test' } ]
        // - an object like A = { B: 'test' }
        //
        // The "has the property" operator translates to "A.B" and explicitly
        // looks for object keys.
        let accessor = accessorType === 'array' ? '?.' : '.';
        if (accessorType === 'object' && pathString.endsWith('.')) {
          // Prevent adding double dots if last character is already a dot
          accessor = '';
        }
        let newString = `${pathString}${accessor}${item}`;
        if (newString.startsWith('.')) {
          // The string can't start with a period - it's not a valid JSON path -
          // remove the period
          newString = newString.substring(1);
        }
        if (newString.startsWith('?.')) {
          // Starting the string with ?. is not a valid JSON path - remove the
          // "?."
          newString = newString.substring(2);
        }
        pathString = newString;
      }
    }
  }

  let depth = 0;

  while (currentCondition != null) {
    updatePathString(
      currentCondition.name,
      currentCondition.arrayConditionType != null ? 'array' : 'object',
      !escapeFirstElement ? depth !== 0 : true,
    );

    lastCondition = currentCondition;

    if (currentCondition.childCondition != null) {
      currentCondition = currentCondition.childCondition;
      depth++;
    } else {
      currentCondition = null;
    }
  }

  if (pathString.endsWith('?.')) {
    // If the string ends with `?.`, remove the period and only leave the
    // question mark at the end to end up with a valid JSON path
    pathString = pathString.slice(0, -1);
  }

  originalCondition.setProperties({
    name: pathString,
    operator: lastCondition.operator,
    comparisonOperator: lastCondition.comparisonOperator,
    value: lastCondition.value,
    value2: lastCondition.value2,
    timestampValue: lastCondition.timestampValue,
    timeDirection: lastCondition.timeDirection,
  });
}
